<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '@/config';

// Reactive data
const workOrders = ref([]);
const filteredWorkOrders = ref([]);
const assets = ref([]);
const loading = ref(false);
const error = ref(null);
const notification = ref({
  show: false,
  message: '',
  type: ''
});

// Filter options
const filterOptions = ref({
  selectedAsset: '',
  fromDate: '',
  toDate: ''
});

// API endpoints
const workOrdersApiUrl = `${API_BASE_URL}/api/workorders/list`;
const workTypesApiUrl = `${API_BASE_URL}/api/worktypes`;
const workTradesApiUrl = `${API_BASE_URL}/api/worktrades`;
const assetsApiUrl = `${API_BASE_URL}/api/assets`;

// Dropdown data for display
const workTypes = ref([]);
const workTrades = ref([]);

// Fetch display data for work types, trades, and assets
const fetchDisplayData = async () => {
  try {
    console.log('Fetching display data...');
    
    const [typesRes, tradesRes, assetsRes] = await Promise.all([
      axios.get(workTypesApiUrl),
      axios.get(workTradesApiUrl),
      axios.get(assetsApiUrl)
    ]);

    workTypes.value = typesRes.data;
    workTrades.value = tradesRes.data;
    
    // Handle assets data - based on your API response structure
    if (Array.isArray(assetsRes.data)) {
      assets.value = assetsRes.data;
      console.log('Assets loaded:', assets.value.length);
      console.log('Sample asset:', assets.value[0]);
    } else {
      console.warn('Unexpected assets response structure:', assetsRes.data);
      assets.value = [];
    }
    
  } catch (err) {
    console.error('Error fetching display data:', err);
    showNotification('Error loading assets data', 'error');
  }
};

// Fetch all work orders
const fetchWorkOrders = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    console.log('Fetching work orders...');
    const response = await axios.get(workOrdersApiUrl);
    workOrders.value = response.data;
    filteredWorkOrders.value = [...workOrders.value];
    
    console.log('Work orders loaded:', workOrders.value.length);
    
    if (workOrders.value.length === 0) {
      showNotification('No work orders found.', 'info');
    }
  } catch (err) {
    console.error('Error fetching work orders:', err);
    error.value = 'Failed to load work orders.';
    if (err.response?.status === 503) {
      error.value = 'Database service unavailable.';
    } else {
      error.value = 'Server Error fetching work orders.';
    }
    showNotification(error.value, 'error');
    workOrders.value = [];
    filteredWorkOrders.value = [];
  } finally {
    loading.value = false;
  }
};

// Apply asset and date filters
const applyFilters = () => {
  let filtered = [...workOrders.value];

  // Filter by selected asset
  if (filterOptions.value.selectedAsset) {
    filtered = filtered.filter(wo => wo.AssetNo === filterOptions.value.selectedAsset);
  }

  // Filter by date range on Start Date
  if (filterOptions.value.fromDate || filterOptions.value.toDate) {
    filtered = filtered.filter(wo => {
      const startDate = new Date(wo.StartDate);
      let fromCondition = true;
      let toCondition = true;

      if (filterOptions.value.fromDate) {
        const fromDate = new Date(filterOptions.value.fromDate);
        fromCondition = startDate >= fromDate;
      }

      if (filterOptions.value.toDate) {
        const toDate = new Date(filterOptions.value.toDate);
        toDate.setHours(23, 59, 59, 999);
        toCondition = startDate <= toDate;
      }

      return fromCondition && toCondition;
    });
  }

  filteredWorkOrders.value = filtered;
};

// Clear all filters
const clearFilters = () => {
  filterOptions.value.selectedAsset = '';
  filterOptions.value.fromDate = '';
  filterOptions.value.toDate = '';
  filteredWorkOrders.value = [...workOrders.value];
};

// Watch for filter changes
watch([
  () => filterOptions.value.selectedAsset,
  () => filterOptions.value.fromDate,
  () => filterOptions.value.toDate
], () => {
  applyFilters();
});

// Show notification helper
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;
  setTimeout(() => {
    notification.value.show = false;
  }, 5000);
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date)) return '-';
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

// Get work type description
const getWorkTypeDescription = (workTypeId) => {
  const workType = workTypes.value.find(wt => wt.WorkTypeID === workTypeId);
  return workType ? workType.WorkTypeDescription : workTypeId;
};

// Get work trade description
const getWorkTradeDescription = (workTradeId) => {
  const workTrade = workTrades.value.find(wt => wt.WorkTradeID === workTradeId);
  return workTrade ? workTrade.WorkTrade : workTradeId;
};

// Get asset description/name
const getAssetDescription = (assetNo) => {
  const asset = assets.value.find(a => a.asset_no === assetNo);
  return asset ? asset.asset_desc : assetNo;
};

// Get selected asset description for display
const getSelectedAssetDescription = () => {
  if (!filterOptions.value.selectedAsset) return 'All Assets';
  const asset = assets.value.find(a => a.asset_no === filterOptions.value.selectedAsset);
  return asset ? `${asset.asset_no} (${asset.asset_desc})` : filterOptions.value.selectedAsset;
};

// Format asset display for dropdown - AssetNo (asset_desc)
const formatAssetDisplay = (asset) => {
  if (asset.asset_no && asset.asset_desc) {
    return `${asset.asset_no} (${asset.asset_desc})`;
  } else if (asset.asset_no) {
    return asset.asset_no;
  } else {
    return 'Unknown Asset';
  }
};

// Export to Excel
const exportToExcel = () => {
  if (filteredWorkOrders.value.length === 0) {
    showNotification('No work orders to export.', 'warning');
    return;
  }

  const dataToExport = filteredWorkOrders.value.map(wo => ({
    'WO No': wo.WorkorderNo,
    'Asset': `${wo.AssetNo} (${getAssetDescription(wo.AssetNo)})`,
    'Location': wo.LocationNo,
    'Work Type': getWorkTypeDescription(wo.WorkType),
    'Actual Start Date': formatDate(wo.ActDateStart),
    'Actual End Date': formatDate(wo.ActDateEnd),
    'Work Trade': getWorkTradeDescription(wo.WOTrade),
    'PM No': wo.PMNo || '-',
    'Task No': wo.TaskNo || '-',
    'Site Code': wo.SiteCode
  }));

  const worksheet = XLSX.utils.json_to_sheet(dataToExport);
  
  // Auto-size columns
  const maxWidth = dataToExport.reduce((w, r) => Math.max(w, Object.values(r).map(v => v ? v.toString().length : 0).reduce((a, b) => Math.max(a, b), 0)), 10);
  worksheet['!cols'] = Array(Object.keys(dataToExport[0] || {}).length).fill({ width: maxWidth });
  
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Work Orders By Asset');

  const fileName = `WorkOrders_Asset_${filterOptions.value.selectedAsset || 'all'}_${filterOptions.value.fromDate || ''}_to_${filterOptions.value.toDate || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(workbook, fileName);
  showNotification(`Exported ${filteredWorkOrders.value.length} work orders to Excel!`, 'success');
};

// Print PDF
const printPDF = () => {
  if (filteredWorkOrders.value.length === 0) {
    showNotification('No data to print.', 'warning');
    return;
  }
  
  // Store original display data for print
  const printData = {
    title: 'Work Orders By Asset Report',
    filters: {
      asset: getSelectedAssetDescription(),
      fromDate: filterOptions.value.fromDate ? formatDate(filterOptions.value.fromDate) : '',
      toDate: filterOptions.value.toDate ? formatDate(filterOptions.value.toDate) : ''
    },
    workOrders: filteredWorkOrders.value,
    workTypes: workTypes.value,
    workTrades: workTrades.value,
    assets: assets.value
  };
  
  // Store in session storage for print window to access
  sessionStorage.setItem('printData', JSON.stringify(printData));
  
  // Open print window
  const printWindow = window.open('', '_blank');
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Work Orders By Asset Report</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .print-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-bottom: 30px; 
          border-bottom: 2px solid #333; 
          padding-bottom: 10px; 
        }
        .logo-left { 
          flex: 1; 
          text-align: left; 
        }
        .logo-right { 
          flex: 1; 
          text-align: right; 
        }
        .logo-img { 
          max-height: 80px; 
          max-width: 150px; 
        }
        .logo-img2 { 
          max-height: 120px; 
          max-width: 120px; 
        }
        .header-center { 
          flex: 2; 
          text-align: center; 
        }
        .print-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
        .print-filters { margin-bottom: 20px; font-size: 14px; color: #666; }
        .print-table { width: 100%; border-collapse: collapse; font-size: 10px; }
        .print-table th, .print-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
        .print-table th { background-color: #f5f5f5; font-weight: bold; }
        .print-table tr:nth-child(even) { background-color: #f9f9f9; }
        @media print {
          body { margin: 0; }
          .print-header { margin-bottom: 20px; }
          .logo-img { max-height: 70px; max-width: 120px; }
          .logo-img2 { max-height: 150px; max-width: 120px; }
          .print-table { font-size: 8px; }
          .print-table th, .print-table td { padding: 3px; }
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <div class="logo-left">
          <img src="../logo.png" alt="Company Logo Left" class="logo-img">
        </div>
        <div class="header-center">
          <div class="print-title">Work Orders By Asset Report</div>
          <div class="print-filters">
            Asset: ${printData.filters.asset} | 
            Date Range: ${printData.filters.fromDate} to ${printData.filters.toDate} | 
            Report Date: ${new Date().toLocaleDateString()} | 
            Total Records: ${printData.workOrders.length}
          </div>
        </div>
        <div class="logo-right">
          <img src="../logo2.png" alt="Company Logo Right" class="logo-img2" style="height = 150px">
        </div>
      </div>

      <table class="print-table">
        <thead>
          <tr>
            <th>WO No</th>
            <th>Asset</th>
            <th>Location</th>
            <th>Work Type</th>
            <th>Actual Start Date</th>
            <th>Actual End Date</th>
            <th>Work Trade</th>
            <th>Site Code</th>
          </tr>
        </thead>
        <tbody>
          ${printData.workOrders.map(wo => {
            const getWorkTypeDescription = (workTypeId) => {
              const workType = printData.workTypes.find(wt => wt.WorkTypeID === workTypeId);
              return workType ? workType.WorkTypeDescription : workTypeId;
            };

            const getWorkTradeDescription = (workTradeId) => {
              const workTrade = printData.workTrades.find(wt => wt.WorkTradeID === workTradeId);
              return workTrade ? workTrade.WorkTrade : workTradeId;
            };

            const getAssetDescription = (assetNo) => {
              const asset = printData.assets.find(a => a.asset_no === assetNo);
              return asset ? asset.asset_desc : assetNo;
            };

            const formatDate = (dateString) => {
              if (!dateString) return '-';
              const date = new Date(dateString);
              if (isNaN(date)) return '-';
              const options = { year: 'numeric', month: 'short', day: 'numeric' };
              return date.toLocaleDateString(undefined, options);
            };

            return `
            <tr>
              <td><strong>${wo.WorkorderNo}</strong></td>
              <td>${wo.AssetNo} (${getAssetDescription(wo.AssetNo)})</td>
              <td>${wo.LocationNo}</td>
              <td>${getWorkTypeDescription(wo.WorkType)}</td>
              <td>${formatDate(wo.ActDateStart)}</td>
              <td>${formatDate(wo.ActDateEnd)}</td>
              <td>${getWorkTradeDescription(wo.WOTrade)}</td>
              <td>${wo.SiteCode}</td>
            </tr>
            `;
          }).join('')}
        </tbody>
      </table>
      <script>
        window.onload = function() {
          window.print();
          setTimeout(() => window.close(), 500);
        };
      <\/script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

// Initialize component
onMounted(() => {
  fetchDisplayData();
  fetchWorkOrders();
});
</script>

<template>
  <div class="container mt-4">
    
    <!-- Print Header -->
    <div class="print-only-header">
      <div class="print-date">Report Date: {{ formatDate(new Date()) }}</div>
    </div>
    
    <h2 class="report-title">Work Orders By Asset</h2>

    <!-- Asset and Date Filters -->
    <div class="card mb-4 print-hidden-element">
      <div class="card-header">
        <h5 class="card-title mb-0">Filter Options</h5>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Asset Dropdown -->
          <div class="col-md-4">
            <label class="form-label">Select Asset</label>
            <select class="form-select" v-model="filterOptions.selectedAsset">
              <option value="">All Assets</option>
              <option 
                v-for="asset in assets" 
                :key="asset.asset_no" 
                :value="asset.asset_no"
              >
                {{ formatAssetDisplay(asset) }}
              </option>
            </select>
            <div v-if="assets.length === 0" class="text-warning small mt-1">
              Loading assets...
            </div>
            <div v-else class="text-muted small mt-1">
              {{ assets.length }} asset(s) available
            </div>
          </div>
          
          <!-- Date Range Filters -->
          <div class="col-md-2">
            <label class="form-label">From Date</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="filterOptions.fromDate"
              :max="filterOptions.toDate || new Date().toISOString().split('T')[0]"
            >
          </div>
          <div class="col-md-2">
            <label class="form-label">To Date</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="filterOptions.toDate"
              :min="filterOptions.fromDate"
              :max="new Date().toISOString().split('T')[0]"
            >
          </div>
          
          <!-- Actions and Counter -->
          <div class="col-md-4 d-flex align-items-end gap-2">
            <button @click="clearFilters" class="btn btn-outline-secondary">
              Clear Filters
            </button>
            <div class="text-muted ms-2">
              Showing {{ filteredWorkOrders.length }} of {{ workOrders.length }} work orders
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="d-flex justify-content-between align-items-center mb-4 print-hidden-element">
      <div class="text-muted">
        <span v-if="filterOptions.selectedAsset">
          Showing work orders for: <strong>{{ getSelectedAssetDescription() }}</strong> |
        </span>
        {{ filteredWorkOrders.length }} work order(s)
        <span v-if="filterOptions.fromDate || filterOptions.toDate" class="ms-2">
          (Date range: 
          <span v-if="filterOptions.fromDate">{{ formatDate(filterOptions.fromDate) }}</span>
          <span v-if="filterOptions.fromDate && filterOptions.toDate"> to </span>
          <span v-if="filterOptions.toDate">{{ formatDate(filterOptions.toDate) }}</span>)
        </span>
      </div>
      <div class="report-actions d-flex gap-2" v-if="filteredWorkOrders.length > 0 && !loading">
        <button @click="exportToExcel" class="btn btn-success">
          <i class="bi bi-file-earmark-spreadsheet"></i> Export To Excel
        </button>
        <button @click="printPDF" class="btn btn-secondary">
          <i class="bi bi-printer"></i> Print as PDF
        </button>
      </div>
    </div>

    <!-- Loading & Error States -->
    <div v-if="loading" class="alert alert-info print-hidden-element">
      <i class="bi bi-arrow-repeat spinner"></i> Loading work orders...
    </div>
    <div v-if="error" class="alert alert-danger print-hidden-element">
      {{ error }}
    </div>
    <div v-if="notification.show" :class="['alert', `alert-${notification.type}`, 'print-hidden-element']">
      {{ notification.message }}
    </div>

    <!-- Work Orders Table -->
    <div v-if="!loading && !error">
      <div v-if="filteredWorkOrders.length > 0" class="table-responsive">
        <table class="table table-striped table-hover report-table">
          <thead>
            <tr>
              <th>WO No</th>
              <th>Asset</th>
              <th>Location</th>
              <th>Work Type</th>
              <th>Actual Start Date</th>
              <th>Actual End Date</th>
              <th>Work Trade</th>
              <th>Site Code</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="wo in filteredWorkOrders" :key="wo.id">
              <td>
                <strong>{{ wo.WorkorderNo }}</strong>
              </td>
              <td>
                <div>{{ getAssetDescription(wo.AssetNo) }}</div>
                <small class="text-muted">{{ wo.AssetNo }}</small>
              </td>
              <td>{{ wo.LocationNo }}</td>
              <td>
                {{ getWorkTypeDescription(wo.WorkType) }}
              </td>
              <td>{{ formatDate(wo.ActDateStart) }}</td>
              <td>{{ formatDate(wo.ActDateEnd) }}</td>
              <td>
                {{ getWorkTradeDescription(wo.WOTrade) }}
              </td>
              <td>{{ wo.SiteCode }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="!loading">
        <div class="alert alert-warning text-center">
          <i class="bi bi-inbox"></i><br>
          <span v-if="filterOptions.selectedAsset || filterOptions.fromDate || filterOptions.toDate">
            No work orders found for the selected filters.
          </span>
          <span v-else>
            No work orders found.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Your existing styles remain the same */
.print-only-header {
  display: none;
}

.badge {
  font-size: 0.75em;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Print Styles */
@media print {
  #app-header-area,
  .app-header,
  .navbar,
  .print-hidden-element,
  .report-actions,
  .alert,
  .badge {
    display: none !important;
  }

  .print-only-header {
    display: block !important;
    text-align: center;
    margin-bottom: 20px;
  }

  .print-date {
    font-size: 10pt;
  }

  .report-title {
    display: block !important;
    text-align: center;
    margin-top: 20px;
    margin-bottom: 30px;
    font-size: 18pt;
    color: black !important;
  }

  .container {
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0 10px;
    box-shadow: none;
  }

  .table-responsive {
    overflow: visible !important;
  }

  .report-table {
    font-size: 9pt;
    width: 100% !important;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .report-table th,
  .report-table td {
    padding: 4px;
    border: 1px solid #ccc;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .table-striped tbody tr:nth-of-type(odd) {
    background-color: #f2f2f2 !important;
  }
}
</style>